import { useEffect, useMemo } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { wrapCreateBrowserRouterV6 } from "@sentry/react";

import { createAppRoutes } from "../../routes/app-routes";
import BonusXLoading from "../../legacy/components/BonusXLoading";
import mixpanel from "../../lib/mixpanel/mixpanel.config";
import { stopSessionRecordingOnAdminRoute } from "../../lib/mixpanel/session-replay/session-replay";
import { startStaleBuildDetection } from "../../lib/version-refresh/stale-build-detector";

const createInstrumentedBrowserRouter = wrapCreateBrowserRouterV6(createBrowserRouter);

export function AppRouter(): JSX.Element {
  const router = useMemo(function createRouter() {
    return createInstrumentedBrowserRouter(createAppRoutes());
  }, []);

  useEffect(
    function setupRouterAndMixpanel() {
      stopSessionRecordingOnAdminRoute(router.state.location.pathname);
      const unsubscribeMixpanel = subscribeToMixpanelPageViews(router);
      const stopStaleBuildDetection = startStaleBuildDetection();

      return function cleanupRouter() {
        stopStaleBuildDetection();
        unsubscribeMixpanel();
        router.dispose();
      };
    },
    [router]
  );

  return <RouterProvider router={router} fallbackElement={<BonusXLoading />} />;
}

function subscribeToMixpanelPageViews(router: ReturnType<typeof createBrowserRouter>): () => void {
  return router.subscribe(function onRouterStateChange(state) {
    const isNavigationComplete = state.navigation.state === "idle";
    if (!isNavigationComplete) {
      return;
    }

    stopSessionRecordingOnAdminRoute(state.location.pathname);

    const isMixpanelAvailable = mixpanel && typeof mixpanel.track_pageview === "function";
    if (isMixpanelAvailable) {
      mixpanel.track_pageview();
    }
  });
}
